/**
 * Team Settings Page
 *
 * Manage team members and invitations.
 *
 * @module pages/settings/TeamPage
 */

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMembers } from '@/hooks/useMembers';
import { usePageTitle } from '@/hooks';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Avatar,
  Modal,
  Spinner,
  Alert,
  useToast,
} from '@/components/ui';
import { InviteMemberModal, UpgradeToInviteModal } from '@/components/team';
import { canInviteTeamMembers } from '@/lib/plans';
import { Plus, MoreVertical, Mail, Trash2, UserCog, LogOut } from 'lucide-react';
import type { OrganizationMember, Invitation, OrgRole, PlanId } from '@/lib/firebase-types';

// =============================================================================
// Helper Functions
// =============================================================================

function formatExpiresIn(timestamp: { toDate?: () => Date } | Date | null | undefined): string {
  if (!timestamp) return '';
  let date: Date;
  if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    return '';
  }
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'Expired';
  if (diffDays === 1) return 'Expires in 1 day';
  return `Expires in ${diffDays} days`;
}

function getRoleBadgeVariant(role: OrgRole): 'primary' | 'default' | 'outline' {
  switch (role) {
    case 'owner':
    case 'org_admin':
      return 'primary';
    case 'billing_admin':
    case 'admin':
      return 'default';
    default:
      return 'outline';
  }
}

/** Role display names */
const ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  org_admin: 'Org Admin',
  billing_admin: 'Billing Admin',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
};

/** Role descriptions for the info section */
const ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  owner: 'Full access. Can transfer ownership and delete the organization.',
  org_admin: 'Full access except transferring ownership or deleting the organization. Can receive ownership.',
  billing_admin: 'Admin powers plus billing management.',
  admin: 'Can manage API keys, view usage, and invite/remove admin, manager, and member roles.',
  manager: 'Can manage API keys, view usage, and invite/remove members only.',
  member: 'View-only access. Can view API keys and usage statistics.',
};

// =============================================================================
// Member Card Component
// =============================================================================

interface MemberCardProps {
  member: OrganizationMember;
  isCurrentUser: boolean;
  canManage: boolean;
  /** Whether the current user is the owner (can manage anyone including owner) */
  currentUserIsOwner: boolean;
  onChangeRole: (userId: string, currentRole: OrgRole) => void;
  onRemove: (userId: string, displayName: string) => void;
}

function MemberCard({
  member,
  isCurrentUser,
  canManage,
  currentUserIsOwner,
  onChangeRole,
  onRemove,
}: MemberCardProps) {
  const [showMenu, setShowMenu] = useState(false);

  // Can only manage this specific member if:
  // - User has general canManage permission
  // - It's not themselves
  // - If the member is the owner, only the owner can manage them (but owner can't manage themselves anyway)
  const canManageThisMember = canManage && !isCurrentUser && (member.role !== 'owner' || currentUserIsOwner);

  return (
    <div className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3">
        <Avatar
          src={member.photoUrl || undefined}
          name={member.displayName || member.email}
          size="md"
        />
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">
              {member.displayName || member.email.split('@')[0]}
            </p>
            {isCurrentUser && <Badge variant="outline">You</Badge>}
          </div>
          <p className="text-sm text-gray-500">{member.email}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Badge variant={getRoleBadgeVariant(member.role)}>{ROLE_LABELS[member.role]}</Badge>

        {canManageThisMember && (
          <div className="relative">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="More options"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 z-20 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  <button
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setShowMenu(false);
                      onChangeRole(member.userId, member.role);
                    }}
                  >
                    <UserCog className="h-4 w-4" />
                    Change Role
                  </button>
                  <button
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error-600 hover:bg-error-50"
                    onClick={() => {
                      setShowMenu(false);
                      onRemove(member.userId, member.displayName || member.email);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove Member
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Invitation Card Component
// =============================================================================

interface InvitationCardProps {
  invitation: Invitation;
  canRevoke: boolean;
  onRevoke: (invitationId: string, email: string) => void;
}

function InvitationCard({ invitation, canRevoke, onRevoke }: InvitationCardProps) {
  return (
    <div className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-full">
          <Mail className="h-5 w-5 text-gray-500" />
        </div>
        <div>
          <p className="font-medium text-gray-900">{invitation.email}</p>
          <p className="text-sm text-gray-500">
            {formatExpiresIn(invitation.expiresAt)} &middot; {ROLE_LABELS[invitation.role]}
          </p>
        </div>
      </div>

      {canRevoke && (
        <Button
          variant="ghost"
          size="sm"
          className="text-error-600"
          onClick={() => onRevoke(invitation.id, invitation.email)}
        >
          Revoke
        </Button>
      )}
    </div>
  );
}

// =============================================================================
// Change Role Modal
// =============================================================================

interface ChangeRoleModalProps {
  open: boolean;
  onClose: () => void;
  currentRole: OrgRole;
  memberName: string;
  /** Roles the current user is allowed to assign */
  assignableRoles: OrgRole[];
  onConfirm: (newRole: OrgRole) => Promise<void>;
}

function ChangeRoleModal({
  open,
  onClose,
  currentRole,
  memberName,
  assignableRoles,
  onConfirm,
}: ChangeRoleModalProps) {
  const [selectedRole, setSelectedRole] = useState<OrgRole>(currentRole);
  const [loading, setLoading] = useState(false);

  // Reset selected role when modal opens with a different member
  const effectiveSelectedRole = assignableRoles.includes(selectedRole) ? selectedRole :
    (assignableRoles.includes(currentRole) ? currentRole : assignableRoles[0]);

  const handleConfirm = async () => {
    if (effectiveSelectedRole === currentRole) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      await onConfirm(effectiveSelectedRole);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change Member Role"
      description={`Update role for ${memberName}. Note: To transfer ownership, use the Transfer Ownership option.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={loading}>
            Update Role
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        {/* Only show roles the current user can assign */}
        {assignableRoles.map((role) => (
          <label
            key={role}
            className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <input
              type="radio"
              name="role"
              value={role}
              checked={effectiveSelectedRole === role}
              onChange={() => setSelectedRole(role)}
              disabled={loading}
              className="mt-0.5"
            />
            <div>
              <div className="font-medium text-gray-900">{ROLE_LABELS[role]}</div>
              <div className="text-sm text-gray-500">
                {ROLE_DESCRIPTIONS[role]}
              </div>
            </div>
          </label>
        ))}
      </div>
    </Modal>
  );
}

// =============================================================================
// Confirm Modal
// =============================================================================

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  confirmText: string;
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => Promise<void>;
}

function ConfirmModal({
  open,
  onClose,
  title,
  description,
  confirmText,
  confirmVariant = 'danger',
  onConfirm,
}: ConfirmModalProps) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant={confirmVariant} onClick={handleConfirm} loading={loading}>
            {confirmText}
          </Button>
        </>
      }
    >
      <p className="text-gray-600">{description}</p>
    </Modal>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function TeamPage() {
  usePageTitle('Team Settings');

  const { user, organization } = useAuth();
  const orgId = organization?.id;
  const toast = useToast();

  const {
    members,
    invitations,
    loading,
    error,
    inviteMember,
    removeMember,
    updateMemberRole,
    revokeInvitation,
    leaveOrganization,
    transferOwnership,
    canManageMembers,
    canInviteMembers,
    canTransferOwnership,
    currentUserRole,
    invitableRoles,
  } = useMembers(orgId);

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Check if the organization's plan allows inviting team members
  // Internal accounts bypass all restrictions
  const planId = (organization?.planId || 'free') as PlanId;
  const isInternalAccount = organization?.accountType === 'internal';
  const canInviteOnPlan = isInternalAccount || canInviteTeamMembers(planId);

  // Handle invite button click - show upgrade modal if Free/Starter (unless internal)
  const handleInviteClick = () => {
    if (canInviteOnPlan) {
      setShowInviteModal(true);
    } else {
      setShowUpgradeModal(true);
    }
  };

  // Selected member/invitation for modals
  const [selectedMember, setSelectedMember] = useState<{
    userId: string;
    name: string;
    role: OrgRole;
  } | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<{
    id: string;
    email: string;
  } | null>(null);

  // Handlers
  const handleInvite = async (email: string, role: OrgRole) => {
    await inviteMember(email, role);
    toast.success(`Invitation sent to ${email}`);
  };

  const handleChangeRole = (userId: string, currentRole: OrgRole) => {
    const member = members.find((m) => m.userId === userId);
    if (member) {
      setSelectedMember({
        userId,
        name: member.displayName || member.email,
        role: currentRole,
      });
      setShowChangeRoleModal(true);
    }
  };

  const handleConfirmChangeRole = async (newRole: OrgRole) => {
    if (!selectedMember) return;
    await updateMemberRole(selectedMember.userId, newRole);
    toast.success(`Role updated for ${selectedMember.name}`);
  };

  const handleRemove = (userId: string, displayName: string) => {
    setSelectedMember({ userId, name: displayName, role: 'member' });
    setShowRemoveModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedMember) return;
    await removeMember(selectedMember.userId);
    toast.success(`${selectedMember.name} has been removed`);
  };

  const handleRevoke = (invitationId: string, email: string) => {
    setSelectedInvitation({ id: invitationId, email });
    setShowRevokeModal(true);
  };

  const handleConfirmRevoke = async () => {
    if (!selectedInvitation) return;
    await revokeInvitation(selectedInvitation.id);
    toast.success(`Invitation to ${selectedInvitation.email} revoked`);
  };

  const handleLeave = async () => {
    await leaveOrganization();
    toast.success('You have left the organization');
    // AuthContext will handle redirect when org membership changes
  };

  // Transfer ownership - find eligible org_admins
  const eligibleForOwnership = members.filter((m) => m.role === 'org_admin');

  const [selectedTransferTarget, setSelectedTransferTarget] = useState<string | null>(null);

  const handleTransferOwnership = async () => {
    if (!selectedTransferTarget) return;
    await transferOwnership(selectedTransferTarget);
    toast.success('Ownership transferred successfully');
    setShowTransferModal(false);
    setSelectedTransferTarget(null);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-error-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Team Members */}
      <Card>
        <CardHeader
          title="Team Members"
          action={
            canInviteMembers && (
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                size="sm"
                onClick={handleInviteClick}
              >
                Invite Member
              </Button>
            )
          }
        />

        <div className="divide-y divide-gray-100">
          {members.map((member) => (
            <MemberCard
              key={member.userId}
              member={member}
              isCurrentUser={member.userId === user?.id}
              canManage={canManageMembers}
              currentUserIsOwner={currentUserRole === 'owner'}
              onChangeRole={handleChangeRole}
              onRemove={handleRemove}
            />
          ))}
        </div>
      </Card>

      {/* Pending Invitations */}
      <Card>
        <CardHeader
          title="Pending Invitations"
        />

        {invitations.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {invitations.map((invitation) => (
              <InvitationCard
                key={invitation.id}
                invitation={invitation}
                canRevoke={canInviteMembers}
                onRevoke={handleRevoke}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No pending invitations</p>
          </div>
        )}
      </Card>

      {/* Roles Info */}
      <Card className="bg-gray-50 border-dashed">
        <CardHeader
          title="Team Roles"
        />

        <div className="space-y-3">
          {(['owner', 'org_admin', 'billing_admin', 'admin', 'manager', 'member'] as OrgRole[]).map((role) => (
            <div key={role} className="flex items-start gap-3">
              <Badge variant={getRoleBadgeVariant(role)} className="whitespace-nowrap">
                {ROLE_LABELS[role]}
              </Badge>
              <p className="text-sm text-gray-600">
                {ROLE_DESCRIPTIONS[role]}
              </p>
            </div>
          ))}
        </div>
      </Card>

      {/* Transfer Ownership */}
      {canTransferOwnership && (
        <Card className="border-warning-200">
          <CardHeader
            title="Transfer Ownership"
          />
          {eligibleForOwnership.length > 0 ? (
            <Button
              variant="secondary"
              onClick={() => setShowTransferModal(true)}
            >
              Transfer Ownership
            </Button>
          ) : (
            <Alert variant="info">
              No Org Admins available. Promote a member to Org Admin first to transfer ownership.
            </Alert>
          )}
        </Card>
      )}

      {/* Leave Organization */}
      {currentUserRole && currentUserRole !== 'owner' && (
        <Card className="border-error-200">
          <CardHeader
            title="Leave Organization"
            description="Remove yourself from this organization"
          />
          <Button
            variant="danger"
            leftIcon={<LogOut className="h-4 w-4" />}
            onClick={() => setShowLeaveModal(true)}
          >
            Leave Organization
          </Button>
        </Card>
      )}

      {/* Modals */}
      <UpgradeToInviteModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      <InviteMemberModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
        invitableRoles={invitableRoles}
      />

      {selectedMember && (
        <ChangeRoleModal
          open={showChangeRoleModal}
          onClose={() => {
            setShowChangeRoleModal(false);
            setSelectedMember(null);
          }}
          currentRole={selectedMember.role}
          memberName={selectedMember.name}
          assignableRoles={invitableRoles}
          onConfirm={handleConfirmChangeRole}
        />
      )}

      <ConfirmModal
        open={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false);
          setSelectedMember(null);
        }}
        title="Remove Team Member"
        description={`Are you sure you want to remove ${selectedMember?.name} from the organization? They will lose access immediately.`}
        confirmText="Remove"
        onConfirm={handleConfirmRemove}
      />

      <ConfirmModal
        open={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setSelectedInvitation(null);
        }}
        title="Revoke Invitation"
        description={`Are you sure you want to revoke the invitation to ${selectedInvitation?.email}? They will no longer be able to join using this link.`}
        confirmText="Revoke"
        onConfirm={handleConfirmRevoke}
      />

      <ConfirmModal
        open={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title="Leave Organization"
        description={`Are you sure you want to leave ${organization?.name}? You will lose access to all resources.`}
        confirmText="Leave"
        onConfirm={handleLeave}
      />

      {/* Transfer Ownership Modal */}
      <Modal
        open={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setSelectedTransferTarget(null);
        }}
        title="Transfer Ownership"
        description="Select an Org Admin to receive ownership. You will become an Org Admin."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowTransferModal(false);
                setSelectedTransferTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleTransferOwnership}
              disabled={!selectedTransferTarget}
            >
              Transfer Ownership
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          {eligibleForOwnership.map((member) => (
            <label
              key={member.userId}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <input
                type="radio"
                name="transferTarget"
                value={member.userId}
                checked={selectedTransferTarget === member.userId}
                onChange={() => setSelectedTransferTarget(member.userId)}
                className="mt-0.5"
              />
              <Avatar
                src={member.photoUrl || undefined}
                name={member.displayName || member.email}
                size="sm"
              />
              <div>
                <div className="font-medium text-gray-900">
                  {member.displayName || member.email.split('@')[0]}
                </div>
                <div className="text-sm text-gray-500">{member.email}</div>
              </div>
            </label>
          ))}
          {eligibleForOwnership.length === 0 && (
            <Alert variant="info" className="my-4">
              No Org Admins available. Promote a member to Org Admin first.
            </Alert>
          )}
        </div>
      </Modal>
    </div>
  );
}
